// Shared registry of user-selectable chat-agent models.
//
// The backend bills + caps turns off a two-value `model_tier` ("basic" | "max")
// and selects the actual model from an allow-list (see config.ALLOWED_AGENT_MODELS).
// Each entry here maps a user-facing model to its OpenRouter slug (sent as
// `model_override`) and its billing tier. The slug list MUST stay in sync with
// the backend allow-list, or the model is silently ignored and falls back to
// the tier default.

export type AgentModelKey =
  | "gemini"
  | "glm"
  | "sonnet"
  | "opus"
  | "fable"
  | "gpt-sol"
  | "gpt-astra";

export interface AgentModelDef {
  key: AgentModelKey;
  /** OpenRouter slug sent to the backend as `model_override`. */
  slug: string;
  /** Billing tier the backend derives credits + USD margin from. */
  tier: "basic" | "max";
  label: string;
  shortLabel: string;
  iconSrc: string;
  /** Official public API price per 1M tokens (same OpenRouter list rates the
   *  usage ledger bills at). Shown to users verbatim, no markup. */
  pricing: { in: number; out: number };
  /** Xynth serves this model at $0 on every plan (Gemini). */
  freeOnXynth?: boolean;
  bullets: string[];
  time: string;
  isNew?: boolean;
  /** Marks the most powerful models we recommend (Opus, Fable). */
  recommended?: boolean;
  /** Single caution line shown under the card (accuracy, price, retention). */
  caution?: string;
}

export const AGENT_MODELS: AgentModelDef[] = [
  {
    key: "gemini",
    slug: "google/gemini-3.8-flash",
    tier: "basic",
    label: "Gemini 3.8 Flash",
    shortLabel: "Gemini",
    iconSrc: "/models/gemini-v2.png",
    pricing: { in: 0.75, out: 3.75 },
    freeOnXynth: true,
    isNew: true,
    bullets: ["Fastest responses", "Best for quick scans and iteration"],
    time: "~1-3 min",
    caution: "Fast and light. Can make things up, so verify numbers.",
  },
  {
    key: "sonnet",
    slug: "anthropic/claude-sonnet-5",
    tier: "max",
    label: "Claude Sonnet 5",
    shortLabel: "Sonnet",
    iconSrc: "/models/claude-v5.png",
    pricing: { in: 3, out: 15 },
    bullets: ["Fastest Claude option", "Reliable for most serious work"],
    time: "~3-6 min",
  },
  {
    key: "opus",
    slug: "anthropic/claude-opus-5.5",
    tier: "max",
    label: "Claude Opus 5.5",
    shortLabel: "Opus",
    iconSrc: "/models/claude-v5.png",
    pricing: { in: 4, out: 20 },
    isNew: true,
    bullets: ["State of the art reasoning", "Best on hard, multi-step tasks"],
    time: "~4-8 min",
  },
  {
    key: "fable",
    slug: "anthropic/claude-fable-5.1",
    tier: "max",
    label: "Claude Fable 5.1",
    shortLabel: "Fable",
    iconSrc: "/models/claude-v5.png",
    pricing: { in: 10, out: 50 },
    isNew: true,
    recommended: true,
    bullets: [
      "A generation beyond Opus",
      "Built for long, autonomous deep work",
    ],
    time: "~6-12 min",
    caution:
      "Roughly 2.5x Opus pricing. Use Opus unless the task is huge.",
  },
  {
    key: "gpt-sol",
    slug: "openai/gpt-6-sol",
    tier: "max",
    label: "GPT-6 Sol",
    shortLabel: "Sol",
    iconSrc: "/models/openai.png",
    pricing: { in: 2, out: 10 },
    isNew: true,
    bullets: ["Balanced GPT option", "Solid on multi-step research"],
    time: "~4-8 min",
  },
  {
    key: "gpt-astra",
    slug: "openai/gpt-6-astra",
    tier: "max",
    label: "GPT-6 Astra",
    shortLabel: "Astra",
    iconSrc: "/models/openai.png",
    pricing: { in: 10, out: 50 },
    isNew: true,
    bullets: ["Most capable GPT option", "Best GPT choice for hard analysis"],
    time: "~6-12 min",
    caution: "Priced like Fable. Slower to start than Claude models.",
  },
];

export const DEFAULT_MODEL_KEY: AgentModelKey = "opus";

const BY_KEY: Record<AgentModelKey, AgentModelDef> = AGENT_MODELS.reduce(
  (acc, m) => {
    acc[m.key] = m;
    return acc;
  },
  {} as Record<AgentModelKey, AgentModelDef>
);

export function modelByKey(key: AgentModelKey): AgentModelDef {
  return BY_KEY[key] ?? BY_KEY[DEFAULT_MODEL_KEY];
}

export function modelBySlug(slug: string): AgentModelDef | undefined {
  return AGENT_MODELS.find((m) => m.slug === slug);
}

/** Fallback model key when no specific model was saved — derive from tier. */
export function modelKeyFromTier(tier: "basic" | "max"): AgentModelKey {
  return tier === "max" ? DEFAULT_MODEL_KEY : "gemini";
}

export function isAgentModelKey(value: unknown): value is AgentModelKey {
  return (
    typeof value === "string" &&
    AGENT_MODELS.some((m) => m.key === value)
  );
}

export const SELECTED_MODEL_STORAGE_KEY = "xynth-selected-model";
